import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API_URL = 'https://europe-west2-everyai-e3397.cloudfunctions.net/api';

function App() {
  const [companions, setCompanions] = useState([]);
  const [selectedCompanion, setSelectedCompanion] = useState(null);
  const [selectedModel, setSelectedModel] = useState('gemini-3.5-flash');
  const [models, setModels] = useState([]);
  const [groupedModels, setGroupedModels] = useState({});
  const [showModels, setShowModels] = useState(false);
  
  // CHANGED: Store messages per companion
  const [chatHistories, setChatHistories] = useState({});
  const [messages, setMessages] = useState([]);
  
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch companions
  useEffect(() => {
    axios.get(`${API_URL}/companions`)
      .then(res => setCompanions(res.data))
      .catch(err => console.error('Error fetching companions:', err));
  }, []);

  // Fetch models and group by category
  useEffect(() => {
    axios.get(`${API_URL}/models`)
      .then(res => {
        const data = res.data;
        setModels(data);
        
        const grouped = {};
        data.forEach(model => {
          const category = model.category || 'Other';
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push(model);
        });
        setGroupedModels(grouped);
      })
      .catch(err => console.error('Error fetching models:', err));
  }, []);

  // CHANGED: When a companion is selected, load their chat history
  const selectCompanion = (companion) => {
    setSelectedCompanion(companion);
    // Load existing messages for this companion, or empty array if none
    setMessages(chatHistories[companion.id] || []);
  };

  // CHANGED: Save messages to the current companion's history
  const saveMessagesToHistory = (companionId, newMessages) => {
    setChatHistories(prev => ({
      ...prev,
      [companionId]: newMessages
    }));
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedCompanion) return;

    const userMessage = { role: 'user', text: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    saveMessagesToHistory(selectedCompanion.id, updatedMessages);
    
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/chat`, {
        message: input,
        companionId: selectedCompanion.id,
        modelId: selectedModel
      });

      const aiMessage = { 
        role: 'assistant', 
        text: res.data.response,
        companion: selectedCompanion.name,
        model: res.data.model
      };
      
      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);
      saveMessagesToHistory(selectedCompanion.id, finalMessages);
      
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  const selectedModelName = models.find(m => m.id === selectedModel)?.name || selectedModel;

  const getCategoryEmoji = (category) => {
    const emojis = {
      'Google': '🔵',
      'OpenAI': '🟢',
      'Anthropic': '🟣',
      'OpenRouter': '🟠',
      'Groq': '🔴',
      'EverlyAI': '🟡',
      'DeepSeek': '🔷',
      'Mistral': '🟤',
      'Cohere': '🟩',
      'Other': '⚪'
    };
    return emojis[category] || '⚪';
  };

  return (
    <div className="app">
      <header className="header">
        <h1>🤖 Companion Hub</h1>
        <p>Chat with different AI personalities</p>
      </header>

      <div className="container">
        <div className="companions-grid">
          <h2>Choose Your Companion</h2>
          <div className="grid">
            {companions.map(companion => {
              // Show message count for each companion
              const historyCount = (chatHistories[companion.id] || []).length;
              return (
                <div 
                  key={companion.id}
                  className={`companion-card ${selectedCompanion?.id === companion.id ? 'selected' : ''}`}
                  onClick={() => selectCompanion(companion)}
                >
                  <div className="emoji">{companion.emoji}</div>
                  <h3>{companion.name}</h3>
                  <p>{companion.description}</p>
                  {historyCount > 0 && (
                    <div className="message-count">💬 {historyCount} messages</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {selectedCompanion && (
          <div className="chat-area">
            <div className="chat-header">
              <span>{selectedCompanion.emoji}</span>
              <h3>Chat with {selectedCompanion.name}</h3>
              <div className="message-count-badge">
                {messages.length} messages
              </div>
              
              <div className="model-selector">
                <button 
                  className="model-selector-button"
                  onClick={() => setShowModels(!showModels)}
                >
                  🧠 {selectedModelName} {showModels ? '▲' : '▼'}
                </button>
                
                {showModels && Object.keys(groupedModels).length > 0 && (
                  <div className="model-dropdown">
                    {Object.entries(groupedModels).map(([category, categoryModels]) => (
                      <div key={category} className="model-category">
                        <div className="model-category-header">
                          {getCategoryEmoji(category)} {category} ({categoryModels.length})
                        </div>
                        {categoryModels.map(model => (
                          <div
                            key={model.id}
                            className={`model-option ${selectedModel === model.id ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedModel(model.id);
                              setShowModels(false);
                            }}
                          >
                            <div className="model-option-name">{model.name}</div>
                            <div className="model-option-description">{model.description}</div>
                            <div className="model-option-provider">{model.provider}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <span className="model-badge">Using {selectedModelName}</span>
            </div>

            <div className="messages">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-emoji">{selectedCompanion.emoji}</span>
                  <h3>Start a conversation with {selectedCompanion.name}</h3>
                  <p>Ask me anything! I'm here to help.</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div key={idx} className={`message ${msg.role}`}>
                    <div className="message-content">
                      {msg.role === 'assistant' && (
                        <span className="companion-emoji">{selectedCompanion.emoji}</span>
                      )}
                      <p>{msg.text}</p>
                      {msg.model && msg.role === 'assistant' && (
                        <div className="message-model">via {msg.model}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="message assistant">
                  <div className="message-content">
                    <span className="companion-emoji">{selectedCompanion.emoji}</span>
                    <p>Thinking...</p>
                  </div>
                </div>
              )}
            </div>

            <div className="input-area">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
              />
              <button onClick={sendMessage} disabled={loading}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;